'use client'

import React, { createContext, useContext, useState, useCallback } from 'react'

export type TaskStatus = 'pending' | 'processing' | 'success' | 'error'

export interface Task {
  id: string
  type: 'url_analysis' | 'file_upload' | 'qa_creation' | 'bulk_upload'
  title: string
  description?: string
  status: TaskStatus
  progress?: number
  error?: string
  createdAt: Date
  completedAt?: Date
}

interface TaskMonitorContextType {
  tasks: Task[]
  addTask: (task: Omit<Task, 'id' | 'createdAt' | 'status'>) => string
  updateTask: (id: string, updates: Partial<Task>) => void
  removeTask: (id: string) => void
  clearCompletedTasks: () => void
}

const TaskMonitorContext = createContext<TaskMonitorContextType | undefined>(undefined)

export function TaskMonitorProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>([])

  const addTask = useCallback((task: Omit<Task, 'id' | 'createdAt' | 'status'>) => {
    const id = `task-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    const newTask: Task = {
      ...task,
      id,
      status: 'pending',
      createdAt: new Date(),
    }
    setTasks(prev => [newTask, ...prev])
    return id
  }, [])

  const updateTask = useCallback((id: string, updates: Partial<Task>) => {
    setTasks(prev => prev.map(task =>
      task.id === id
        ? { ...task, ...updates, completedAt: updates.status === 'success' || updates.status === 'error' ? new Date() : task.completedAt }
        : task
    ))
  }, [])

  const removeTask = useCallback((id: string) => {
    setTasks(prev => prev.filter(task => task.id !== id))
  }, [])

  const clearCompletedTasks = useCallback(() => {
    setTasks(prev => prev.filter(task => task.status === 'pending' || task.status === 'processing'))
  }, [])

  return (
    <TaskMonitorContext.Provider value={{ tasks, addTask, updateTask, removeTask, clearCompletedTasks }}>
      {children}
    </TaskMonitorContext.Provider>
  )
}

export function useTaskMonitor() {
  const context = useContext(TaskMonitorContext)
  if (!context) {
    throw new Error('useTaskMonitor must be used within TaskMonitorProvider')
  }
  return context
}
